import { useRouter } from 'next/router'
import { useCallback, useContext, useEffect, useState } from 'react'
import { getEnv } from '../../../config/index'
import { FirebaseAuth } from '../../../lib/firebase'
import { eraseCookie, setCookie } from '../../../lib/cookies'
import ReactGA from '../../../lib/react-ga'
import { UserContext } from '../../../types/user-context.types'
import { postHogIdentify } from '../../../lib/posthog'
import { ContextShell } from '../../../modules/context'

interface UseSetupAnalytics {
  onUserLoaded(user: UserContext): void
}

export const useSetupAnalytics = (): UseSetupAnalytics => {
  const { pathname } = useRouter()
  const [configured, setConfigured] = useState(false)
  const {
    event: {
      rudderstack: {
        identify: rudderstackIdentify,
        page: rudderstackPage,
        isReady
      }
    }
  } = useContext(ContextShell)

  useEffect(() => {
    const testMode = getEnv() !== 'production'

    ReactGA.initialize('G-VCCHPVZC99', {
      testMode
    })
  }, [])

  useEffect(() => {
    if (isReady) {
      rudderstackPage()
    }
  }, [isReady, pathname, rudderstackPage])

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: pathname
    })
    global?.document.body.setAttribute('data-page', pathname.split('/')[1])
  }, [pathname])

  const onUserLoaded = useCallback(
    (user: UserContext) => {
      if (!user.id || configured) return

      ReactGA.set({ dimension1: user.id })
      ReactGA.set({ userId: user.id })

      rudderstackIdentify(user.id)
      postHogIdentify(user)

      try {
        if (user.shouldIdentifyOnCIO) {
          // @ts-ignore
          _cio.identify({
            id: user.id,
            email: user.email
          })
        }
      } catch (error) {
        // @ts-ignore
        console.log('CIO error', error) // eslint-disable-line no-console
      }

      // hotjar
      global?.window?.hj('identify', user.id, {
        createdAt: user?.createdAt,
        isPremium: user.subscription?.isPremium || false,
        currentMonthlyUsage: user?.subscription?.currentMonthlyUsage,
        isPro: user.subscription?.isPro || false,
        locale:
          navigator.languages && navigator.languages.length
            ? navigator.languages[0]
            : // @ts-ignore
              navigator.userLanguage ||
              navigator.language ||
              // @ts-ignore
              navigator.browserLanguage ||
              'unset'
      })

      // facebook pixel
      global.window.fbq('init', '828020674704420', { external_id: user.id })

      setConfigured(true)
      //  disable toast verify email
      // setUserHasEmailVerified(!!user.emailVerified)
    },
    [configured, rudderstackIdentify]
  )

  useEffect(() => {
    const unsubscribe = FirebaseAuth.onAuthStateChanged((user) => {
      if (!user) {
        setConfigured(false)
        eraseCookie('moises:user:auth')
      } else {
        setCookie({
          days: 365,
          value: 'true',
          name: 'moises:user:auth'
        })
      }
    })

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [])

  return {
    onUserLoaded
  }
}
